import { components, paths } from "@/api-schema";
import {
  PathsV1TemporaryPermissionSearchPostParametersQueryExtend,
  PathsV1TemporaryPermissionSearchPostParametersQueryOrderByDirection,
  PathsV1TemporaryPermissionSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useTemporaryPermissionDeleteMutation } from "@/common/api/temporary-permission/use-temporary-permission-delete.mutation";
import { useTemporaryPermissionSearchQuery } from "@/common/api/temporary-permission/use-temporary-permission-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import { TemporaryPermissionSubComponent } from "@/modules/ram/temporary/_components/temporary-permission-sub-component";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { temporaryPermissionTableDefaultColumns } from "./temporary-permission-table-columns";

type TemporaryPermission = components["schemas"]["TemporaryPermission"] & RowWithIdentifier;
type TemporaryPermissionSorting = NonNullable<
  paths["/v1/temporary-permission/search"]["post"]["parameters"]["query"]["orderBy"]
>;
type TemporaryPermissionQueryParams = paths["/v1/temporary-permission/search"]["post"]["parameters"]["query"];
type TemporaryPermissionRequestBody =
  paths["/v1/temporary-permission/search"]["post"]["requestBody"]["content"]["application/json"];
type TemporaryPermissionExtend = NonNullable<
  paths["/v1/temporary-permission/search"]["post"]["parameters"]["query"]["extend"]
>;

const DEFAULT_EXTEND: TemporaryPermissionExtend = [
  PathsV1TemporaryPermissionSearchPostParametersQueryExtend.RequestBy,
  PathsV1TemporaryPermissionSearchPostParametersQueryExtend.RequestFor,
  PathsV1TemporaryPermissionSearchPostParametersQueryExtend.DecidedBy,
] as const;

const DEFAULT_COLUMNS = [
  temporaryPermissionTableDefaultColumns.columnRequestBy,
  temporaryPermissionTableDefaultColumns.columnRequestFor,
  temporaryPermissionTableDefaultColumns.columnValidFrom,
  temporaryPermissionTableDefaultColumns.columnValidUntil,
  temporaryPermissionTableDefaultColumns.columnStatus,
  temporaryPermissionTableDefaultColumns.columnActions,
];

const TEMPORARY_PERMISSION_TABLE_CONFIG: PredefinedTableConfig<TemporaryPermissionSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1TemporaryPermissionSearchPostParametersQueryOrderByDirection.Desc,
      field: PathsV1TemporaryPermissionSearchPostParametersQueryOrderByField.UpdatedAt,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: "common:SearchTemporaryPermissions",
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: true,
    } as TableFeatures,
  } as const,
  defaultToastMessages: {
    deleteSuccess: "common:TemporaryPermissionDeleted",
    deleteError: "common:ErrorDeletingTemporaryPermission",
  } as const,
  defaultRowActions: {
    showEdit: false,
    showView: false,
  } as const,
  sortingMapping: {
    valid_from: ["valid_from"],
    valid_until: ["valid_until"],
    status: ["status"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
} as const;

const TEMPORARY_PERMISSION_TABLE_QUERY_HOOKS: DataFetchHooks<
  TemporaryPermission,
  TemporaryPermissionQueryParams,
  TemporaryPermissionRequestBody
> = {
  useSearchQuery: useTemporaryPermissionSearchQuery,
  useDeleteMutation: useTemporaryPermissionDeleteMutation,
} as const;

export interface TemporaryPermissionTableProps extends BasePredefinedTableProps<
  TemporaryPermission,
  TemporaryPermissionSorting,
  TemporaryPermissionQueryParams,
  TemporaryPermissionRequestBody
> {
  extend?: TemporaryPermissionExtend;
  // Custom actions for temporary permissions
  onApprove?: (uuid: string) => void;
  onReject?: (uuid: string) => void;
}

const BaseTemporaryPermissionTable = createBasePredefinedTable({
  config: TEMPORARY_PERMISSION_TABLE_CONFIG,
  queryHooks: TEMPORARY_PERMISSION_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function TemporaryPermissionTable(props: TemporaryPermissionTableProps) {
  const { t } = useTranslation(["common", "ram"]);
  const {
    name = "temporary-permission-table",
    extend = DEFAULT_EXTEND,
    onApprove,
    onReject,
    query,
    subComponent = TemporaryPermissionSubComponent,
    getRowCanExpand,
    meta = { onApprove, onReject },
    ...restProps
  } = props;

  const enhancedQuery = query
    ? {
        ...query,
        extend: query.extend ?? extend,
      }
    : undefined;

  return (
    <BaseTemporaryPermissionTable
      name={name}
      query={enhancedQuery}
      extend={extend}
      subComponent={subComponent}
      getRowCanExpand={getRowCanExpand}
      meta={meta}
      searchPlaceholder={t("common:SearchTemporaryPermissions")}
      toastMessages={{
        deleteSuccess: t("common:TemporaryPermissionDeleted"),
        deleteError: t("common:ErrorDeletingTemporaryPermission"),
      }}
      {...restProps}
    />
  );
}
